import { FiArrowLeft, FiBell, FiCheckCircle, FiClock, FiHash, FiMail, FiPhone, FiShoppingBag, FiUser, FiXCircle } from "react-icons/fi";
import { Link, useParams } from "react-router-dom";
import { getRegisteredUserSnapshot } from "../../../../../api/adminMarketingUsers";
import { ADMIN_PERMISSIONS, useAdminAuth } from "../../../../../providers/adminAuthentication.provider";
import { buyerInitials } from "../../marketing-buyers/marketing-buyers.data";
import { BuyerDetailsWrapper } from "../buyer-details.style";

const currency = (value) => Number(value || 0).toLocaleString("en-NG", { style: "currency", currency: "NGN", maximumFractionDigits: 0 });
const formatDateTime = (value) => {
  if (!value) return "Not available";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleString("en-NG", { dateStyle: "medium", timeStyle: "short" });
};
const available = (value) => Boolean(value && value !== "Not available");

const ChannelStatus = ({ icon: Icon, label, value, detail }) => (
  <div className={`channel-status ${value ? "available" : "unavailable"}`}>
    <span className="channel-icon"><Icon aria-hidden="true" /></span>
    <span><small>{label}</small><strong>{value ? "Available" : "Unavailable"}</strong><p>{detail}</p></span>
    {value ? <FiCheckCircle className="channel-check" aria-label="Available" /> : <FiXCircle className="channel-check" aria-label="Unavailable" />}
  </div>
);

export const BuyerDetailsSection = ({ customerType = "registered" }) => {
  const { buyerId } = useParams();
  const { role } = useAdminAuth();
  const buyer = getRegisteredUserSnapshot(buyerId);
  const canMessage = ADMIN_PERMISSIONS.MARKETING.includes(role);
  const isGuestUser = buyer?.accountKind === "guest" || customerType === "guest";
  const directoryPath = `/admin/internal/customers/${isGuestUser ? "guests" : "registered"}`;
  const directoryLabel = isGuestUser ? "guest users" : "registered users";

  if (!buyer) return <BuyerDetailsWrapper><Link className="back-link" to={directoryPath}><FiArrowLeft /> Back to {directoryLabel}</Link><section className="empty-profile"><h1>User not found</h1><p>Open this user from its customer directory so the current API record can be loaded.</p></section></BuyerDetailsWrapper>;

  const hasEmail = available(buyer.email);
  const hasPhone = available(buyer.phone);

  return (
    <BuyerDetailsWrapper>
      <Link className="back-link" to={directoryPath}><FiArrowLeft /> Back to {directoryLabel}</Link>

      <header className="profile-header">
        <div className="profile-identity"><span className="profile-avatar" aria-hidden="true">{buyerInitials(buyer.name)}</span><div><span className="overline">{isGuestUser ? "Guest user" : "Registered user"}</span><h1>{buyer.name}</h1><p>{buyer.email} · ID {buyer.id}</p><span className="engagement-pill active">{buyer.type}</span></div></div>
        <div className="profile-actions">{hasPhone && <a className="secondary-action" href={`tel:${buyer.phone}`}><FiPhone /> Call</a>}</div>
      </header>

      <section className="profile-metrics" aria-label={`${isGuestUser ? "Guest" : "Registered"} user activity`}>
        <article><span>Total orders</span><strong>{buyer.orders.toLocaleString()}</strong><small>Orders returned by the API</small></article>
        <article><span>Total amount spent</span><strong>{currency(buyer.totalSpend)}</strong><small>Recorded purchase value</small></article>
        <article><span>Last active</span><strong className="date-value">{formatDateTime(buyer.lastActiveAt)}</strong><small>Latest recorded activity</small></article>
        <article><span>Last order</span><strong className="date-value">{formatDateTime(buyer.lastOrderAt)}</strong><small>Most recent order date</small></article>
      </section>

      <div className="profile-grid">
        <section className="profile-card">
          <div className="card-heading"><div><h2>Account details</h2><p>The customer fields returned by the marketing audience endpoint.</p></div></div>
          <div className="info-list">
            <div><span className="info-icon"><FiUser /></span><span><small>First name</small><strong>{buyer.firstname || "Not available"}</strong></span></div>
            <div><span className="info-icon"><FiUser /></span><span><small>Last name</small><strong>{buyer.lastname || "Not available"}</strong></span></div>
            <div><span className="info-icon"><FiMail /></span><span><small>Email address</small>{hasEmail ? <a href={`mailto:${buyer.email}`}>{buyer.email}</a> : <strong>Not available</strong>}</span></div>
            <div><span className="info-icon"><FiPhone /></span><span><small>Phone number</small>{hasPhone ? <a href={`tel:${buyer.phone}`}>{buyer.phone}</a> : <strong>Not available</strong>}</span></div>
            <div><span className="info-icon"><FiHash /></span><span><small>User ID</small><strong>{buyer.id}</strong></span></div>
            <div><span className="info-icon"><FiShoppingBag /></span><span><small>Role</small><strong>{buyer.type}</strong></span></div>
            <div><span className="info-icon"><FiClock /></span><span><small>Last active</small><strong>{formatDateTime(buyer.lastActiveAt)}</strong></span></div>
            <div><span className="info-icon"><FiShoppingBag /></span><span><small>Last order date</small><strong>{formatDateTime(buyer.lastOrderAt)}</strong></span></div>
          </div>
        </section>

        <section className="profile-card">
          <div className="card-heading"><div><h2>Communication availability</h2><p>Channels this user can currently receive based on the API record.</p></div></div>
          <div className="channel-list">
            <ChannelStatus icon={FiMail} label="Email" value={hasEmail} detail={hasEmail ? buyer.email : "No email address supplied"} />
            <ChannelStatus icon={FiPhone} label="SMS or call" value={hasPhone} detail={hasPhone ? buyer.phone : "No phone number supplied"} />
            <ChannelStatus icon={FiBell} label="Push notification" value={buyer.hasPushToken} detail={buyer.hasPushToken ? "FCM token is available" : "No FCM token is available"} />
          </div>
          {!canMessage && <p className="permission-note">Campaign messaging is available to marketing administrators. Operations can review this user’s account and channel availability.</p>}
        </section>
      </div>
    </BuyerDetailsWrapper>
  );
};
